import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";
import { Button } from "primereact/button";
import { useCallback, useMemo, useState } from "react";
import ObligationDetailsModal from "./components/ObligationDetailsModal";
import useAsyncEffect from "../../hooks/useAsyncEffect"
import useObligationsSearchForm from "../../hooks/useObligationsSearchForm";
import agent from "../../agent";
import styles from "./style.module.scss";

const initialPageConfig = {
    page: 0,
    pageSize: 10,
    totalElements: 0,
};

const normalizeFilters = (raw) => {
    const trim = (v) => (typeof v === "string" ? v.trim() : v);

    const toNum = (v) => {
        const t = trim(v);
        if (t === "" || t == null) return null;
        const n = Number(t);
        return Number.isFinite(n) ? n : null;
    };

    const payload = {
        obligation_id: toNum(raw.obligation_id),
        operation_type: trim(raw.operation_type) || null,
        tax_type_id: toNum(raw.tax_type_id),
        tax_type_name: trim(raw.tax_type_name) || null,
        driver_id: toNum(raw.driver_id),
        driver_iin: trim(raw.driver_iin) || null,
        executor_name: trim(raw.executor_name) || null,
        month: raw.month ?? null,
        year: toNum(raw.year),
    };

    // remove nulls
    return Object.fromEntries(Object.entries(payload).filter(([, v]) => v !== null && v !== ""));
};

const ObligationsJournal = () => {
    const [tableData, setTableData] = useState([]);
    const [pageConfig, setPageConfig] = useState(initialPageConfig);
    const [searchParams, setSearchParams] = useState(null);

    const [detailsVisible, setDetailsVisible] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);

    const { searchForm, handleSubmit, reset } = useObligationsSearchForm();

    const fetchTableData = useCallback(async () => {
        const { page, pageSize } = pageConfig;
        let response;

        if (!searchParams) {
            response = await agent.ObligationsJournal.list(page, pageSize);
        } else {
            response = await agent.ObligationsJournal.search(searchParams, page, pageSize);
        }

        const content = response?.content ?? [];
        const total = response?.totalElements ?? 0;

        setTableData(content);
        setPageConfig((prev) => (prev.totalElements === total ? prev : { ...prev, totalElements: total }));
    }, [pageConfig, searchParams]);

    useAsyncEffect(fetchTableData, [fetchTableData]);

    const onSearch = handleSubmit((filters) => {
        const normalized = normalizeFilters(filters);

        if (!Object.keys(normalized).length) {
            setSearchParams(null);
        } else {
            setSearchParams(normalized);
        }

        setPageConfig((prev) => ({ ...prev, page: 0 }));
    });

    const onReset = useCallback(() => {
        reset();
        setSearchParams(null);
        setPageConfig((prev) => ({ ...prev, page: 0 }));
    }, [reset]);

    const onPageChange = useCallback(
        (e) => {
            setPageConfig((prev) => ({
                ...prev,
                page: e.page,
                pageSize: e.rows,
            }));
        },
        [setPageConfig]
    );

    const openDetails = useCallback((row) => {
        setSelectedRow(row);
        setDetailsVisible(true);
    }, []);

    const closeDetails = useCallback(() => {
        setDetailsVisible(false);
        setSelectedRow(null);
    }, []);

    const actionsBody = useCallback((row) => {
        const disabled = row?.canRecalculate !== true; // по ТЗ кнопка обычно не активна
        return (
            <Button
                icon="pi pi-refresh"
                text
                rounded
                disabled={disabled}
                tooltip={disabled ? "Перерасчет недоступен" : "Перерасчет"}
                onClick={(e) => {
                    e.stopPropagation();
                    // TODO: когда появится логика перерасчета:
                    // agent.ObligationsJournal.recalculate(row.obligation_id)
                    //   .then(fetchTableData)
                    //   .catch(...)
                }}
            />
        );
    }, []);

    const columns = useMemo(
        () => [
            { field: "obligation_id", header: "ID обязательства" },
            { field: "operation_type", header: "Тип операции" },
            { field: "tax_type_id", header: "ID вида налога/платежа" },
            { field: "tax_type_name", header: "Наименование вида налога/платежа" },
            { field: "driver_id", header: "ID водителя" },
            { field: "driver_iin", header: "ИИН водителя" },
            { field: "executor_name", header: "ФИО водителя" },
            { field: "month", header: "Месяц расчета" },
            { field: "year", header: "Год расчета" },
        ],
        []
    );

    return (
        <div className={styles.page}>
            <h2 className={styles.title}>Журнал обязательств</h2>

            <div className={styles.filterPanel}>
                {searchForm(
                    <div className={styles.filterActions}>
                        <Button label="Поиск" icon="pi pi-search" onClick={onSearch} />
                        <Button label="Сбросить" severity="warning" onClick={onReset} />
                    </div>
                )}
            </div>

            <div className={styles.tableWrap}>
                <DataTable
                    value={tableData}
                    className={styles.dataTable}
                    stripedRows
                    rowHover
                    onRowClick={(e) => openDetails(e.data)}
                    emptyMessage="Нет данных"
                >
                    {columns.map((c) => (
                        <Column key={c.field} field={c.field} header={c.header} />
                    ))}
                    <Column header="Действия" body={actionsBody} />
                </DataTable>

                <Paginator
                    className={styles.paginator}
                    currentPageReportTemplate="Показано с {first} по {last} из {totalRecords} записей"
                    template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    first={pageConfig.page * pageConfig.pageSize}
                    rows={pageConfig.pageSize}
                    totalRecords={pageConfig.totalElements}
                    rowsPerPageOptions={[10, 25, 50]}
                    onPageChange={onPageChange}
                />
            </div>

            <ObligationDetailsModal
                visible={detailsVisible}
                obligationId={selectedRow?.obligation_id}
                onHide={closeDetails}
            />
        </div>
    );
};

export default ObligationsJournal;